export const ANALYTICS_SAMPLE_SIZE = 500;

const SCREEN_LABELS: Record<string, string> = {
  dashboard: 'Dashboard',
  traffic: 'Traffic',
  inspector: 'Inspector',
  costs: 'Costs',
  packaging: 'Packaging',
  questions: 'Questions',
  populations: 'Populations',
  tables: 'Tables',
  formulas: 'Formulas',
  onboarding: 'Branding',
  content: 'Content',
  branding: 'Adv Branding',
  configuration: 'Configuration',
  superadmin: 'Super Admin',
  'questions-admin': "Q's Admin",
  analytics: 'Analytics',
  sessions: 'Sessions',
  'api-errors': 'API Errors',
  ':schoolId': 'Home (legacy)',
};

export type ActivityAudience = 'all' | 'school' | 'support';

export interface ActivityRecord {
  email: string;
  path: string;
  at: Date | null;
}

export interface CollapsedActivity {
  email: string;
  path: string;
  screen: string;
  count: number;
  latest: Date | null;
  earliest: Date | null;
  schoolStaff: boolean;
}

export interface ScreenCount {
  path: string;
  screen: string;
  count: number;
  heightPct: number;
}

export interface AnalyticsSummary {
  lastSchoolVisit: { email: string; at: Date; screen: string } | null;
  schoolUserCount: number;
  topScreen: { screen: string; count: number } | null;
  screenCounts: ScreenCount[];
}

export function pathKey(path: string): string {
  const trimmed = (path || '').replace(/^\//, '').trim();
  if (!trimmed) {
    return 'dashboard';
  }

  return trimmed.split('/')[0] || 'dashboard';
}

export function screenLabel(path: string): string {
  const key = pathKey(path);
  return SCREEN_LABELS[key] ?? titleCase(key);
}

export function isLikelySupportEmail(email: string): boolean {
  const lower = email.trim().toLowerCase();
  if (!lower) {
    return false;
  }

  return lower.endsWith('@rewiretech.com') || lower.endsWith('@collegeboard.org');
}

export function isSchoolStaffEmail(email: string, schoolEmails: Set<string>): boolean {
  const key = email.trim().toLowerCase();
  if (!key) {
    return false;
  }

  if (schoolEmails.size > 0) {
    return schoolEmails.has(key);
  }

  return !isLikelySupportEmail(key);
}

export function collapseActivity(
  records: ActivityRecord[],
  schoolEmails: Set<string>
): CollapsedActivity[] {
  const collapsed: CollapsedActivity[] = [];

  for (const record of records) {
    const key = pathKey(record.path);
    const email = record.email.trim() || 'Unknown user';
    const last = collapsed[collapsed.length - 1];

    if (last && last.email.toLowerCase() === email.toLowerCase() && last.path === key) {
      last.count += 1;
      if (record.at && (!last.earliest || record.at < last.earliest)) {
        last.earliest = record.at;
      }
      continue;
    }

    collapsed.push({
      email,
      path: key,
      screen: screenLabel(record.path),
      count: 1,
      latest: record.at,
      earliest: record.at,
      schoolStaff: isSchoolStaffEmail(email, schoolEmails),
    });
  }

  return collapsed;
}

export function filterCollapsedActivity(
  rows: CollapsedActivity[],
  audience: ActivityAudience
): CollapsedActivity[] {
  if (audience === 'school') {
    return rows.filter(row => row.schoolStaff);
  }

  if (audience === 'support') {
    return rows.filter(row => !row.schoolStaff);
  }

  return rows;
}

export function summarizeSchoolActivity(
  records: ActivityRecord[],
  schoolEmails: Set<string>
): AnalyticsSummary {
  const staff = records.filter(record => isSchoolStaffEmail(record.email, schoolEmails));
  const uniqueStaff = new Set(
    staff
      .map(record => record.email.trim().toLowerCase())
      .filter(Boolean)
  );

  let lastSchoolVisit: AnalyticsSummary['lastSchoolVisit'] = null;
  for (const record of records) {
    if (isSchoolStaffEmail(record.email, schoolEmails) && record.at) {
      lastSchoolVisit = {
        email: record.email,
        at: record.at,
        screen: screenLabel(record.path),
      };
      break;
    }
  }

  const counts = new Map<string, number>();
  for (const record of staff) {
    const key = pathKey(record.path);
    counts.set(key, (counts.get(key) ?? 0) + 1);
  }

  const ranked = [...counts.entries()]
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6);
  const max = ranked[0]?.[1] ?? 0;

  return {
    lastSchoolVisit,
    schoolUserCount: uniqueStaff.size,
    topScreen: ranked[0]
      ? { screen: screenLabel(ranked[0][0]), count: ranked[0][1] }
      : null,
    screenCounts: ranked.map(([path, count]) => ({
      path,
      screen: screenLabel(path),
      count,
      heightPct: max > 0 ? Math.max(8, Math.round((count / max) * 100)) : 0,
    })),
  };
}

function titleCase(value: string): string {
  return value
    .replace(/[-_]/g, ' ')
    .replace(/\b\w/g, character => character.toUpperCase());
}
